import { useId, useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { IconTile } from '@/shared/components';
import { I, type IconName } from '@/shared/icons';
import { cn } from '@/shared/lib/cn';

interface CollapsibleProps {
  icon: IconName;
  title: string;
  /** Kurz-Zusammenfassung rechts neben dem Titel (sichtbar wenn zu). */
  summary?: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
}

// Zusammenklappbare Card fuer sekundaere Detail-Bloecke (Sync-IDs, Consent-Trail).
// Standard zu — haelt die Detail-Ansicht kompakt.
export function Collapsible({
  icon,
  title,
  summary,
  defaultOpen = false,
  children,
}: CollapsibleProps) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();
  return (
    <div className="border-hairline rounded-tile overflow-hidden border bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={panelId}
        className="hover:bg-paper flex min-h-14 w-full items-center gap-3 px-5 py-4 text-left transition-colors"
      >
        <IconTile variant="muted" size="sm" className="shrink-0">
          <I name={icon} size={14} />
        </IconTile>
        <span className="font-serif text-base font-bold">{title}</span>
        {summary && (
          <span className="text-ink-faint ml-1 min-w-0 flex-1 truncate text-xs">{summary}</span>
        )}
        <ChevronDown
          size={16}
          strokeWidth={2}
          className={cn(
            'text-ink-faint ml-auto shrink-0 transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>
      {open && (
        <div id={panelId} className="border-hairline border-t px-5 py-4">
          {children}
        </div>
      )}
    </div>
  );
}
